import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { X, Check, Hotel, Home, Share2, Phone, FileDown } from "lucide-react";
import { CalculatorInputs, Currency } from "./calculator/types";
import { calculateAllResults, formatCurrency } from "./calculator/calculations";
import BookingModal from "@/components/BookingModal";
import { toast } from "sonner";
import jsPDF from "jspdf";
import guestlyLogo from "@/assets/guestly-logo-horizontal-white.png";
import { triggerHaptic } from "@/lib/haptics";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CorporateCalculatorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CorporateCalculatorModal = ({ open, onOpenChange }: CorporateCalculatorModalProps) => {
  const { t } = useTranslation();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  
  const [inputs, setInputs] = useState<CalculatorInputs>({
    employees: 4,
    nights: 14,
    hotelRate: 1950,
    guestlyRate: 1050,
    extraFees: 0,
    includeGymValue: true,
    currency: "SEK",
  });

  // Load inputs from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlInputs: Partial<CalculatorInputs> = {};
    
    if (params.has('employees')) urlInputs.employees = parseInt(params.get('employees')!);
    if (params.has('nights')) urlInputs.nights = parseInt(params.get('nights')!);
    if (params.has('hotelRate')) urlInputs.hotelRate = parseInt(params.get('hotelRate')!);
    if (params.has('guestlyRate')) urlInputs.guestlyRate = parseInt(params.get('guestlyRate')!);
    if (params.has('extraFees')) urlInputs.extraFees = parseInt(params.get('extraFees')!);
    if (params.has('includeGymValue')) urlInputs.includeGymValue = params.get('includeGymValue') === 'true';
    if (params.has('currency')) urlInputs.currency = params.get('currency') as Currency;
    
    if (Object.keys(urlInputs).length > 0) {
      setInputs(prev => ({ ...prev, ...urlInputs }));
    }
  }, []);

  const results = calculateAllResults(inputs);
  const isNegativeSavings = results.directSavings < 0;

  const updateInput = (key: keyof CalculatorInputs, value: number | boolean | Currency) => {
    if (key === "currency" && typeof value === "string") {
      const oldCurrency = inputs.currency;
      const newCurrency = value as Currency;
      
      // Conversion rates from SEK
      const conversionRates: Record<Currency, number> = {
        SEK: 1,
        EUR: 0.088,
        USD: 0.095,
      };
      
      // Calculate conversion factor between currencies
      const conversionFactor = conversionRates[newCurrency] / conversionRates[oldCurrency];
      
      // Convert the rates
      const newHotelRate = Math.round(inputs.hotelRate * conversionFactor);
      const newGuestlyRate = Math.round(inputs.guestlyRate * conversionFactor);
      const newExtraFees = Math.round(inputs.extraFees * conversionFactor);
      
      setInputs((prev) => ({ 
        ...prev, 
        currency: newCurrency,
        hotelRate: newHotelRate,
        guestlyRate: newGuestlyRate,
        extraFees: newExtraFees,
      }));
    } else {
      setInputs((prev) => ({ ...prev, [key]: value }));
    }
  };

  const generateShareableUrl = () => {
    const params = new URLSearchParams();
    params.set('employees', inputs.employees.toString());
    params.set('nights', inputs.nights.toString());
    params.set('hotelRate', inputs.hotelRate.toString());
    params.set('guestlyRate', inputs.guestlyRate.toString());
    params.set('extraFees', inputs.extraFees.toString());
    params.set('includeGymValue', inputs.includeGymValue.toString());
    params.set('currency', inputs.currency);
    
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    navigator.clipboard.writeText(url);
    
    // Success haptic feedback
    triggerHaptic('success');
    toast.success(t("corporateCalculator.shareSuccess") || "Link copied to clipboard!");
  };

  const generatePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Guestly Brand Colors
    const brandPink = [255, 62, 91]; // #FF3E5B
    const brandNavy = [43, 45, 66]; // #2B2D42
    const brandYellow = [255, 179, 0]; // #FFB300
    const brandTeal = [0, 113, 115]; // #007173
    
    // Header with Logo and Brand Color
    doc.setFillColor(brandPink[0], brandPink[1], brandPink[2]);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Add logo (horizontal white logo)
    try {
      const logoImg = new Image();
      logoImg.src = guestlyLogo;
      await new Promise((resolve) => {
        logoImg.onload = resolve;
      });
      // Calculate proper aspect ratio for horizontal logo (approx 3:1 ratio)
      const logoWidth = 50;
      const logoHeight = logoWidth / 3;
      doc.addImage(logoImg, 'PNG', 20, 12, logoWidth, logoHeight);
    } catch (error) {
      console.error('Logo loading error:', error);
      // Fallback text if logo fails
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.text('Guestly Homes', 20, 25);
    }
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(t('corporateCalculator.pdf.title'), pageWidth - 20, 25, { align: 'right' });
    
    let yPos = 50;
    
    // Executive Summary
    doc.setTextColor(brandNavy[0], brandNavy[1], brandNavy[2]);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(t('corporateCalculator.pdf.executiveSummary'), 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    const summaryText = isNegativeSavings
      ? t('corporateCalculator.pdf.summaryNegative', { amount: formatCurrency(Math.abs(results.directSavings), inputs.currency) })
      : t('corporateCalculator.pdf.summaryPositive', { amount: formatCurrency(results.totalAdvantage, inputs.currency) });
    
    const splitSummary = doc.splitTextToSize(summaryText, pageWidth - 40);
    doc.text(splitSummary, 20, yPos);
    yPos += splitSummary.length * 5 + 10;
    
    // Two-column layout: Input Parameters (left) and Cost Breakdown (right)
    const leftColX = 20;
    const rightColX = pageWidth / 2 + 5;
    const colWidth = pageWidth / 2 - 25;
    const startYPos = yPos;
    
    // Left Column: Input Parameters
    doc.setTextColor(brandNavy[0], brandNavy[1], brandNavy[2]);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(t('corporateCalculator.pdf.inputParameters'), leftColX, yPos);
    yPos += 7;
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    const nightsLabel = inputs.nights === 1 ? t('corporateCalculator.pdf.night') : t('corporateCalculator.pdf.nights');
    const params = [
      `${t('corporateCalculator.pdf.employees')}: ${inputs.employees}`,
      `${t('corporateCalculator.pdf.duration')}: ${inputs.nights} ${nightsLabel}`,
      `${t('corporateCalculator.pdf.hotelRate')}: ${formatCurrency(inputs.hotelRate, inputs.currency)}/${nightsLabel}`,
      `${t('corporateCalculator.pdf.guestlyRate')}: ${formatCurrency(inputs.guestlyRate, inputs.currency)}/${nightsLabel}`,
      `${t('corporateCalculator.pdf.extraFees')}: ${formatCurrency(inputs.extraFees, inputs.currency)}`,
      `${t('corporateCalculator.pdf.gym')}: ${inputs.includeGymValue ? t('corporateCalculator.pdf.yes') : t('corporateCalculator.pdf.no')}`,
    ];
    
    params.forEach(param => {
      doc.text(`• ${param}`, leftColX + 2, yPos);
      yPos += 5;
    });
    
    // Right Column: Cost Breakdown
    let rightYPos = startYPos;
    doc.setTextColor(brandNavy[0], brandNavy[1], brandNavy[2]);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(t('corporateCalculator.pdf.costBreakdown'), rightColX, rightYPos);
    rightYPos += 7;
    
    // Hotel Costs
    doc.setFillColor(239, 68, 68);
    doc.rect(rightColX, rightYPos - 3, 60, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(t('corporateCalculator.pdf.hotelAccommodations'), rightColX + 2, rightYPos + 2);
    rightYPos += 9;
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Base: ${formatCurrency(results.hotelCost - (inputs.extraFees * inputs.employees * inputs.nights), inputs.currency)}`, rightColX + 2, rightYPos);
    rightYPos += 5;
    doc.text(`Fees: ${formatCurrency(inputs.extraFees * inputs.employees * inputs.nights, inputs.currency)}`, rightColX + 2, rightYPos);
    rightYPos += 5;
    doc.setFont(undefined, 'bold');
    doc.setTextColor(239, 68, 68);
    doc.text(`${t('corporateCalculator.pdf.totalCost')}: ${formatCurrency(results.hotelCost, inputs.currency)}`, rightColX + 2, rightYPos);
    rightYPos += 10;
    
    // Guestly Costs
    doc.setFillColor(brandTeal[0], brandTeal[1], brandTeal[2]);
    doc.rect(rightColX, rightYPos - 3, 60, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(t('corporateCalculator.pdf.guestlySolution'), rightColX + 2, rightYPos + 2);
    rightYPos += 9;
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Base: ${formatCurrency(results.guestlyCost, inputs.currency)}`, rightColX + 2, rightYPos);
    rightYPos += 5;
    if (results.gymValue > 0) {
      doc.setTextColor(brandTeal[0], brandTeal[1], brandTeal[2]);
      doc.text(`+ ${t('corporateCalculator.pdf.gymMembership')}: ${formatCurrency(results.gymValue, inputs.currency)}`, rightColX + 2, rightYPos);
      rightYPos += 5;
    }
    doc.setFont(undefined, 'bold');
    doc.setTextColor(brandTeal[0], brandTeal[1], brandTeal[2]);
    doc.text(`${t('corporateCalculator.pdf.totalCost')}: ${formatCurrency(results.guestlyCost, inputs.currency)}`, rightColX + 2, rightYPos);
    
    // Move yPos to continue after both columns
    yPos = Math.max(yPos, rightYPos) + 10;
    
    // Comparison Chart
    doc.setTextColor(brandNavy[0], brandNavy[1], brandNavy[2]);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Cost Comparison', 20, yPos);
    yPos += 8;
    
    const chartHeight = 50;
    const chartWidth = pageWidth - 40;
    const chartX = 20;
    const chartY = yPos;
    
    // Calculate bar heights (proportional to costs)
    const maxCost = Math.max(results.hotelCost, results.guestlyCost);
    const hotelBarHeight = (results.hotelCost / maxCost) * chartHeight;
    const guestlyBarHeight = (results.guestlyCost / maxCost) * chartHeight;
    
    // Draw Hotel bar
    const hotelBarX = chartX + chartWidth * 0.25 - 25;
    doc.setFillColor(239, 68, 68);
    doc.rect(hotelBarX, chartY + chartHeight - hotelBarHeight, 50, hotelBarHeight, 'F');
    
    // Hotel label and value
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(brandNavy[0], brandNavy[1], brandNavy[2]);
    doc.text(t('corporateCalculator.pdf.hotelAccommodations'), hotelBarX + 25, chartY + chartHeight + 6, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(239, 68, 68);
    doc.text(formatCurrency(results.hotelCost, inputs.currency), hotelBarX + 25, chartY + chartHeight - hotelBarHeight - 4, { align: 'center' });
    
    // Draw Guestly bar
    const guestlyBarX = chartX + chartWidth * 0.75 - 25;
    doc.setFillColor(brandTeal[0], brandTeal[1], brandTeal[2]);
    doc.rect(guestlyBarX, chartY + chartHeight - guestlyBarHeight, 50, guestlyBarHeight, 'F');
    
    // Guestly label and value
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(brandNavy[0], brandNavy[1], brandNavy[2]);
    doc.text(t('corporateCalculator.pdf.guestlySolution'), guestlyBarX + 25, chartY + chartHeight + 6, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(brandTeal[0], brandTeal[1], brandTeal[2]);
    doc.text(formatCurrency(results.guestlyCost, inputs.currency), guestlyBarX + 25, chartY + chartHeight - guestlyBarHeight - 4, { align: 'center' });
    
    yPos += chartHeight + 15;
    
    // Savings Highlight Box
    const savingsBoxHeight = 22;
    if (isNegativeSavings) {
      doc.setFillColor(239, 68, 68);
    } else {
      doc.setFillColor(brandPink[0], brandPink[1], brandPink[2]);
    }
    doc.rect(20, yPos - 5, pageWidth - 40, savingsBoxHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    const savingsLabel = isNegativeSavings ? t('corporateCalculator.pdf.savings') : t('corporateCalculator.pdf.savingsHighlights');
    doc.text(savingsLabel, pageWidth / 2, yPos + 3, { align: 'center' });
    doc.setFontSize(22);
    doc.text(formatCurrency(Math.abs(results.totalAdvantage), inputs.currency), pageWidth / 2, yPos + 14, { align: 'center' });
    yPos += savingsBoxHeight + 12;
    
    // Key Benefits Section
    if (!isNegativeSavings && yPos < pageHeight - 60) {
      doc.setTextColor(brandNavy[0], brandNavy[1], brandNavy[2]);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(t('corporateCalculator.pdf.keyBenefits'), 20, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      const benefits = [
        `✓ ${t('corporateCalculator.pdf.benefit1')}`,
        `✓ ${t('corporateCalculator.pdf.benefit2')}`,
        `✓ ${t('corporateCalculator.pdf.benefit3')}`,
        `✓ ${t('corporateCalculator.pdf.benefit4')}`,
      ];
      
      benefits.forEach(benefit => {
        doc.text(benefit, 25, yPos);
        yPos += 6;
      });
    }
    
    // Footer
    const footerY = pageHeight - 20;
    doc.setFillColor(brandNavy[0], brandNavy[1], brandNavy[2]);
    doc.rect(0, footerY - 5, pageWidth, 30, 'F');
    
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text('Generated by Guestly Homes Cost Calculator', pageWidth / 2, footerY + 2, { align: 'center' });
    doc.text(new Date().toLocaleDateString(), pageWidth / 2, footerY + 8, { align: 'center' });
    doc.setTextColor(brandPink[0], brandPink[1], brandPink[2]);
    doc.text('Visit guestlyhomes.com for more information', pageWidth / 2, footerY + 14, { align: 'center' });
    
    // Save the PDF
    doc.save('guestly-savings-report.pdf');
    
    // Success haptic feedback for PDF download
    triggerHaptic('success');
    toast.success(t('corporateCalculator.pdf.savePdfButton') + ' ✓');
  };

  const hotelItems = t("corporateCalculator.comparison.hotelItems", {
    returnObjects: true,
  }) as string[];
  const guestlyItems = t("corporateCalculator.comparison.guestlyItems", {
    returnObjects: true,
  }) as string[];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="text-2xl font-bold">
            {t("corporateCalculator.intro.headline")}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            {t("corporateCalculator.intro.subheadline")}
          </p>
        </DialogHeader>
        
        <div className="px-6 pb-6 space-y-6">
          {/* Compact Comparison */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Hotel className="w-5 h-5 text-destructive" />
                <h3 className="font-bold text-sm">{t("corporateCalculator.comparison.hotelTitle")}</h3>
              </div>
              <ul className="space-y-2">
                {hotelItems.slice(0, 3).map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-xs">
                    <X className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
              <div className="flex items-center gap-2 mb-3">
                <Home className="w-5 h-5 text-primary" />
                <h3 className="font-bold text-sm">{t("corporateCalculator.comparison.guestlyTitle")}</h3>
              </div>
              <ul className="space-y-2">
                {guestlyItems.slice(0, 3).map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-xs">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Currency Selector */}
          <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
            <Label htmlFor="currency" className="text-sm font-medium">
              {t("corporateCalculator.calculator.inputs.currency")}
            </Label>
            <Select
              value={inputs.currency}
              onValueChange={(value: Currency) => updateInput("currency", value)}
            >
              <SelectTrigger className="w-[120px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="SEK">SEK (kr)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
                <SelectItem value="USD">USD ($)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Compact Calculator */}
          <div className="bg-card rounded-lg border p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Employees */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="employees" className="text-sm">
                    {t("corporateCalculator.calculator.inputs.employees")}
                  </Label>
                  <Input
                    id="employees"
                    type="number"
                    min="1"
                    max="50"
                    value={inputs.employees}
                    onChange={(e) => updateInput("employees", parseInt(e.target.value) || 1)}
                    className="w-16 h-8 text-sm text-right"
                  />
                </div>
                <Slider
                  min={1}
                  max={50}
                  step={1}
                  value={[inputs.employees]}
                  onValueChange={([value]) => updateInput("employees", value)}
                />
              </div>

              {/* Nights */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="nights" className="text-sm">
                    {t("corporateCalculator.calculator.inputs.nights")}
                  </Label>
                  <Input
                    id="nights"
                    type="number"
                    min="1"
                    max="365"
                    value={inputs.nights}
                    onChange={(e) => updateInput("nights", parseInt(e.target.value) || 1)}
                    className="w-16 h-8 text-sm text-right"
                  />
                </div>
                <Slider
                  min={1}
                  max={365}
                  step={1}
                  value={[inputs.nights]}
                  onValueChange={([value]) => updateInput("nights", value)}
                />
              </div>

              {/* Hotel Rate */}
              <div className="space-y-1">
                <Label htmlFor="hotelRate" className="text-sm">
                  {t("corporateCalculator.calculator.inputs.hotelRate", { currency: inputs.currency })}
                </Label>
                <Input
                  id="hotelRate"
                  type="number"
                  min="0"
                  step="100"
                  value={inputs.hotelRate}
                  onChange={(e) => updateInput("hotelRate", parseInt(e.target.value) || 0)}
                  className="h-8 text-sm"
                />
              </div>

              {/* Guestly Rate */}
              <div className="space-y-1">
                <Label htmlFor="guestlyRate" className="text-sm">
                  {t("corporateCalculator.calculator.inputs.guestlyRate", { currency: inputs.currency })}
                </Label>
                <Input
                  id="guestlyRate"
                  type="number"
                  min="0"
                  step="100"
                  value={inputs.guestlyRate}
                  onChange={(e) => updateInput("guestlyRate", parseInt(e.target.value) || 0)}
                  className="h-8 text-sm"
                />
              </div>

              {/* Extra Fees */}
              <div className="space-y-1">
                <Label htmlFor="extraFees" className="text-sm">
                  {t("corporateCalculator.calculator.inputs.extraFees")}
                </Label>
                <Input
                  id="extraFees"
                  type="number"
                  min="0"
                  step="50"
                  value={inputs.extraFees}
                  onChange={(e) => updateInput("extraFees", parseInt(e.target.value) || 0)}
                  className="h-8 text-sm"
                />
              </div>

              {/* Gym Toggle */}
              <div className="flex items-center justify-between">
                <Label htmlFor="includeGym" className="text-sm">
                  {t("corporateCalculator.calculator.inputs.includeGym")}
                </Label>
                <Switch
                  id="includeGym"
                  checked={inputs.includeGymValue}
                  onCheckedChange={(checked) => updateInput("includeGymValue", checked)}
                />
              </div>
            </div>
          </div>

          {/* Compact Results */}
          <div className="bg-card rounded-lg border p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t("corporateCalculator.calculator.results.hotelCost")}
                </p>
                <p className="text-xl font-bold text-foreground">
                  {formatCurrency(results.hotelCost, inputs.currency)}
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t("corporateCalculator.calculator.results.guestlyCost")}
                </p>
                <p className="text-xl font-bold text-foreground">
                  {formatCurrency(results.guestlyCost, inputs.currency)}
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t("corporateCalculator.calculator.results.directSavings")}
                </p>
                <p className={`text-xl font-bold ${isNegativeSavings ? "text-destructive" : "text-primary"}`}>
                  {isNegativeSavings ? "-" : "+"}
                  {formatCurrency(Math.abs(results.directSavings), inputs.currency)}
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t("corporateCalculator.calculator.results.gymValue")}
                </p>
                <p className="text-xl font-bold text-primary">
                  +{formatCurrency(results.gymValue, inputs.currency)}
                </p>
              </div>
            </div>

            <div className="bg-primary/10 border border-primary rounded-lg p-3 mt-3">
              <p className="text-xs text-muted-foreground mb-1">
                {t("corporateCalculator.calculator.results.totalAdvantage")}
              </p>
              <p className="text-2xl font-bold text-primary">
                {results.totalAdvantage >= 0 ? "+" : "-"}
                {formatCurrency(Math.abs(results.totalAdvantage), inputs.currency)}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={generatePDF}
                className="flex-1"
              >
                <FileDown className="w-4 h-4 mr-2" />
                {t("corporateCalculator.pdf.savePdfButton")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={generateShareableUrl}
                className="flex-1"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {t("corporateCalculator.shareButton")}
              </Button>
              <Button
                size="sm"
                onClick={() => setBookingModalOpen(true)}
                className="flex-1"
              >
                <Phone className="w-4 h-4 mr-2" />
                {t("corporateCalculator.bookCallButton") || "Book a Call"}
              </Button>
            </div>
          </div>

          {/* Compact Narrative */}
          <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground space-y-2">
            <p>
              {isNegativeSavings
                ? t("corporateCalculator.calculator.narrative.negativeSavings", {
                    amount: formatCurrency(Math.abs(results.directSavings), inputs.currency),
                  })
                : t("corporateCalculator.calculator.narrative.savings", {
                    amount: formatCurrency(results.directSavings, inputs.currency),
                  })}
            </p>
            {results.gymValue > 0 && (
              <p>
                {t("corporateCalculator.calculator.narrative.gym", {
                  amount: formatCurrency(results.gymValue, inputs.currency),
                })}
              </p>
            )}
          </div>
        </div>
      </DialogContent>

      <BookingModal
        open={bookingModalOpen}
        onOpenChange={setBookingModalOpen}
      />
    </Dialog>
  );
};

export default CorporateCalculatorModal;
